import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { InputWithIcon } from '@tempo-io/sui-input';
import { Slider } from '@tempo-io/sui-slider';
import type { ChangeEvent, KeyboardEvent, WheelEvent as ReactWheelEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FlexDiv } from './Styled';

export const limitDecimalPlaces = (rawValue: string, places: number | null | undefined): string => {
  const maxDecimalPlaces = Math.max(places ?? 0, 0);
  const dotIndex = rawValue.indexOf('.');
  if (dotIndex === -1) return rawValue;
  if (maxDecimalPlaces === 0) return rawValue.slice(0, dotIndex);
  return rawValue.slice(0, dotIndex + 1 + maxDecimalPlaces);
};

export const isDecimalSeparatorBlocked = (
  key: string,
  places: number | null | undefined,
): boolean => Math.max(places ?? 0, 0) === 0 && (key === '.' || key === ',');

const getPercentRangeClass = (percent: number) => {
  if (percent === 0) return undefined;
  if (percent >= 80) return '[&_[data-slot=slider-range]]:bg-bg-success-subtle';
  if (percent >= 60) return '[&_[data-slot=slider-range]]:bg-bg-warning-subtle';
  if (percent >= 30) return '[&_[data-slot=slider-range]]:bg-bg-warning';
  return '[&_[data-slot=slider-range]]:bg-bg-error';
};

export const PercentFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const { t: a11y } = useTranslation('translation');
  if (fieldConfiguration.fieldType !== 'PERCENT') return null;

  const { places, minimum, maximum, increment } = fieldConfiguration;
  const sliderMin = minimum ?? 0;
  const sliderMax = maximum ?? 100;
  const sliderStep = increment || 1;

  const onSliderChange = (newValue: number[]) => {
    const [next] = newValue;
    if (next === undefined) return;
    setValues([limitDecimalPlaces(next.toString(), places)]);
  };
  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValues([limitDecimalPlaces(event.target.value, places)]);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isDecimalSeparatorBlocked(event.key, places)) {
      event.preventDefault();
    }
  };

  const numberInputOnWheelPreventChange = (e: ReactWheelEvent<HTMLInputElement>) => {
    if (document.activeElement !== e.target) return;

    (e.target as HTMLInputElement).blur();

    e.preventDefault();
    e.stopPropagation();

    setTimeout(() => {
      (e.target as HTMLInputElement).focus();
    }, 0);
  };

  const value = values[0] ? parseFloat(values[0]) : sliderMin;

  if (isEditableText) {
    return (
      <Slider
        className={cn(
          'fs-exclude [&_[data-slot=slider-track]]:h-6 [&_[data-slot=slider-track]]:rounded-xs',
          getPercentRangeClass(value),
        )}
        data-dd-action-name="FIM 3 Custom Field - slider redacted"
        aria-label={a11y(($) => $.intake.a11y.percentSlider)}
        showTooltip
        value={[value]}
        onValueChange={onSliderChange}
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        {...rest}
      />
    );
  }

  return (
    <FlexDiv>
      <div className="mr-3 w-[100px] shrink-0">
        <InputWithIcon
          className="fs-exclude"
          data-dd-action-name="FIM 3 Custom Field - percent redacted"
          data-testid="fim3-custom-field-input-percent-input-field"
          aria-label={a11y(($) => $.intake.a11y.percentField)}
          title=""
          value={values[0] ?? ''}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onWheel={numberInputOnWheelPreventChange}
          type="number"
          step={increment ?? undefined}
          min={minimum ?? undefined}
          max={maximum ?? undefined}
          rightIcon={<span>%</span>}
          {...rest}
        />
      </div>
      <Slider
        aria-label={a11y(($) => $.intake.a11y.percentSlider)}
        showTooltip
        value={[value]}
        onValueChange={onSliderChange}
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
      />
    </FlexDiv>
  );
};
